import React, { useEffect, useState } from 'react';
import styles from './ErrorView.module.scss';
import Button from '../common/Button/Button';
import ErrorIcon from '../common/assets/error.svg';
import { ApplicationResolution } from '../applicationResolution';
import { cls } from '../View/utils';
import { getErrorMessages } from '../error/errorUtils';
import { useMouseListeners } from '../hooks/useMouseListeners';
import {
  blur as blurOnMouseMove,
  fromCoords,
  triggerFocus,
} from '../navigation/utils';
import { Focus } from '../navigation/typings';
import { getTranslation } from '../localizations/translations';
import { ErrorName } from '../error/errors';
import LoginIcon from '../common/assets/login.svg';
import { useEventListener } from '../hooks/useEventListener';
import { Keys } from '../utils/keys';
import { useProfilesLocalStorage } from '../ProfilesLocalStorageContext';
import { Profile } from '../../common/types';
import { useNavigateWithHistory } from '../Routes/useNavigateWithHistory';
import { HOME_PATH, LOGIN_PATH, PROFILES_PATH } from '../Routes/routeNames';
import { useLocationWithState } from '../Routes/useLocationWithState';
import { getFunctionalityAnalytics } from '../analytics/analytics-utils';
import { AppURLSearchParams } from '../Routes/searchParamKeys';
import { appUrlSearchParamsToString } from '../Routes/utils';
import {
  InputMethod,
  useInputMethodContext,
} from '../contexts/InputMethodContext/InputMethodContext';
import { useAppFeaturesContext } from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { useAnalytics } from '../contexts/AnalyticsContext/useAnalytics';
import { useAuthContext } from '../contexts/AuthContext/AuthContext';
import { useSidebarContext } from '../contexts/SidebarContext/SidebarContext';

const useErrorViewNavigation = (inputMethod: InputMethod) => {
  const [focus] = useState<Focus>(
    fromCoords({
      row: 0,
      column: 0,
      offset: 0,
    })
  );

  useEffect(() => {
    triggerFocus(focus, inputMethod === 'keyboard');
  }, [focus, inputMethod]);

  useMouseListeners(200, () => blurOnMouseMove(focus));
};

const continueButtonTestId = 'error-view_continue-button';

const ErrorView = () => {
  const { languageCode } = useAppFeaturesContext();
  const { profiles, deleteProfileByUserId } = useProfilesLocalStorage();
  const location = useLocationWithState();
  const { inputMethod } = useInputMethodContext();
  const { sendPageloadAnalytics } = useAnalytics();
  const { clearSession } = useAuthContext();
  const { hideSidebar } = useSidebarContext();

  const error = location?.state?.error;

  useEffect(() => {
    function onViewLoad() {
      hideSidebar();

      const analytics = getFunctionalityAnalytics(
        `error_${errorName}`,
        languageCode
      );

      sendPageloadAnalytics(analytics);
    }

    onViewLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorName = error?.name || ErrorName.UNKNOWN_ERROR;

  let profile: Profile | undefined;
  let buttonLabel = getTranslation(
    'ErrorView_refreshButton_label',
    languageCode
  );

  if (errorName === ErrorName.EXPIRED_SESSION_ERROR) {
    profile = profiles.find(
      p => p.cookie === error?.sessionCookie || p.userId === error?.userId
    );
    buttonLabel = getTranslation(
      'ErrorView_sessionExpiredError_buttonLabel',
      languageCode
    );
    clearSession();
  }

  useEffect(() => {
    if (profile && errorName === ErrorName.EXPIRED_SESSION_ERROR) {
      deleteProfileByUserId(profile.userId);
    }
  }, [profile, deleteProfileByUserId, errorName]);

  const navigate = useNavigateWithHistory();

  function onClick() {
    if (
      error?.name === ErrorName.EXPIRED_SESSION_ERROR &&
      profiles.length > 0
    ) {
      navigate(`/${PROFILES_PATH}`, { replace: true });
    } else if (
      error?.name === ErrorName.EXPIRED_SESSION_ERROR &&
      profiles.length === 0
    ) {
      navigate(`/${LOGIN_PATH}`, { replace: true });
    } else {
      navigate(`/${HOME_PATH}`, { replace: true });
    }
  }

  const onKeyDownEvent = (e: KeyboardEvent) => {
    if (e.keyCode === Keys.VK_ENTER) {
      (e.target as HTMLElement).click();
      e.preventDefault(); // avoid double click events triggering, depending on runtime device
    }
  };

  useEventListener('keydown', onKeyDownEvent, document);

  return (
    <ErrorComponent
      buttonLabel={buttonLabel}
      errorName={errorName}
      inputMethod={inputMethod}
      onButtonClick={onClick}
      profileDisplayName={profile?.displayName}
    />
  );
};

type ComponentProps = {
  buttonLabel?: string;
  errorName: string;
  inputMethod?: InputMethod;
  onButtonClick?: () => void;
  profileDisplayName?: string;
};

function fallbackButtonOnClick(params: AppURLSearchParams) {
  const urlSearchParams = appUrlSearchParamsToString(params);

  window.location.replace(`/?${urlSearchParams}`);
}

// exported as ErrorBoundary's fallback component
export const ErrorComponent: React.FC<ComponentProps> = ({
  buttonLabel,
  errorName,
  inputMethod = 'keyboard',
  onButtonClick,
  profileDisplayName,
}) => {
  const { applicationResolution, languageCode, e2eTests } =
    useAppFeaturesContext();

  const { title, description } = getErrorMessages(
    errorName,
    languageCode,
    profileDisplayName
  );

  useErrorViewNavigation(inputMethod);

  const onClick =
    onButtonClick ||
    (() =>
      fallbackButtonOnClick({
        resolution: applicationResolution,
        e2eTests,
      }));

  const text = [
    title,
    buttonLabel ||
      getTranslation('ErrorView_refreshButton_label', languageCode),
  ].join(' ');

  return (
    <div className={styles.root}>
      <div className={styles.viewContainer}>
        <div className={styles.messageContent}>
          <div
            className={cls({
              [styles.iconHD]:
                applicationResolution === ApplicationResolution.HD,
              [styles.iconFullHD]:
                applicationResolution === ApplicationResolution.FULLHD,
            })}
          >
            {errorName === ErrorName.EXPIRED_SESSION_ERROR ? (
              <LoginIcon />
            ) : (
              <ErrorIcon />
            )}
          </div>
          <div className={styles.textContainer}>
            <h1 className={styles.title}>{title}</h1>
            <p className={styles.description}>{description}</p>
          </div>
        </div>

        <div className={styles.buttonContainer}>
          <Button
            type={'primary'}
            id={`0/0/0`}
            onClick={onClick}
            text={
              buttonLabel ||
              getTranslation('ErrorView_refreshButton_label', languageCode)
            }
            dataTestId={continueButtonTestId}
            aria-label={text}
          ></Button>
        </div>
      </div>
    </div>
  );
};

export default ErrorView;
